/**
 * =============================================================================
 * Componente: ProgramTermsDialog
 * Versão: 1.0.0 (2026-05-02)
 *
 * Descrição:
 *   Modal que exibe os Termos do Programa (modelo de contrato vinculado)
 *   reaproveitando ContractFullPreview do módulo CRM. Espelho de
 *   CampaignTermsDialog, mas para Programas.
 *
 *   Suporta dois modos de carregamento:
 *     1. `programId`  → busca via RPC `get_program_terms_for_affiliate`
 *                       (afiliado autenticado já cadastrado).
 *     2. `prefetched` → recebe o shape já pronto (cadastro público,
 *                       onde get_public_program_by_slugs já retornou os
 *                       campos do template — evita RPC adicional).
 *
 *   Responsividade:
 *     - Fullscreen real no mobile (100vw x 100dvh, sem padding lateral).
 *     - 95vw / 85vh em ≥ sm (padrão do projeto).
 * =============================================================================
 */
import { Loader2, FileText, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ContractFullPreview } from '@/components/contracts/ContractFullPreview';
import {
  useProgramTermsForAffiliate,
  type ProgramTermsData,
} from '@/hooks/useProgramTermsForAffiliate';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, carrega via RPC (afiliado autenticado). */
  programId?: string | null;
  /** Quando informado, usa direto sem chamar RPC (cadastro público). */
  prefetched?: ProgramTermsData | null;
  programName?: string | null;
}

export function ProgramTermsDialog({
  open,
  onOpenChange,
  programId = null,
  prefetched = null,
  programName,
}: Props) {
  // Carrega via RPC apenas quando NÃO houver dados pré-carregados.
  const shouldFetch = !prefetched && !!programId;
  const {
    data: fetched,
    isLoading,
    isError,
    error,
  } = useProgramTermsForAffiliate(programId, shouldFetch && open);

  const data = prefetched ?? fetched ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          p-0 gap-0 flex flex-col overflow-hidden
          w-screen h-[100dvh] max-w-none rounded-none
          sm:w-[95vw] sm:h-[85vh] sm:max-w-4xl sm:rounded-lg
        "
      >
        <DialogHeader className="px-3 sm:px-4 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="shrink-0">Termos do Programa</span>
            {programName && (
              <span className="text-muted-foreground font-normal truncate min-w-0">
                — {programName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden p-2 sm:p-4">
          {shouldFetch && isLoading && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Carregando termos...
            </div>
          )}

          {shouldFetch && isError && !isLoading && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
              <p className="text-sm text-muted-foreground">
                {(error as Error | null)?.message ||
                  'Não foi possível carregar os termos deste programa.'}
              </p>
            </div>
          )}

          {data && (
            <ContractFullPreview
              contentHtml={data.content_html}
              backgroundImagePath={data.background_image_path}
              coverPdfPath={data.cover_pdf_path}
              basePdfPath={data.base_pdf_path}
              basePdfPosition={data.base_pdf_position}
              showPageNumbers={data.show_page_numbers}
              pageNumberPosition={data.page_number_position ?? 'bottom-right'}
              bgMarginTopMm={data.bg_margin_top_mm ?? undefined}
              bgMarginBottomMm={data.bg_margin_bottom_mm ?? undefined}
              paginationYOffsetMm={data.pagination_y_offset_mm ?? undefined}
              className="h-full"
            />
          )}

          {!data && !isLoading && !isError && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Nenhum termo cadastrado para este programa.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
